import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { sessionAtom } from "../../features/accounts/account.state";
import { EntityType } from "../../features/data/data-types";
import { AccountRef } from "../../features/accounts/account";

export interface ExhaustionInfo {
  current: number;
  limit: number;
  pct: number;
  isExhausted: boolean;
  isNear: boolean; // > 80%
}

/**
 * Check exhaustion state for a specific entity type.
 * Returns current usage, limit, and whether user is exhausted or near limit.
 */
export function useExhaustionCheck(type: EntityType): ExhaustionInfo | null {
  const session = useAtomValue(sessionAtom);

  return useMemo(() => {
    const account = session?.account;
    if (!account) return null;
    return getExhaustionInfo(account, type);
  }, [session?.account, type]);
}

/**
 * Pure function to get exhaustion info from account data.
 * Can be used outside of React (e.g. in grid-func.ts callbacks).
 */
export function getExhaustionInfo(
  account: AccountRef,
  type: EntityType,
): ExhaustionInfo | null {
  const { limits, summary } = account;
  if (!limits || !summary) return null;

  let current: number;
  let limit: number;

  switch (type) {
    case "products":
      current = summary.itemsCount;
      limit = limits.items ?? 0;
      break;
    case "catalogs":
      current = summary.catalogsCount;
      limit = limits.catalogs ?? 0;
      break;
    case "images":
      current = summary.imageSizeMb;
      limit = limits.space ?? 0;
      break;
    case "users":
      current = summary.activeUsersCount;
      limit = limits.users ?? 0;
      break;
    case "variants":
      current = 0; // checked per-product, not account-level
      limit = limits.variants;
      break;
    case "pages":
      current = 0; // checked per-catalog, not account-level
      limit = limits.pageLimit;
      break;
    case "stores":
      current = summary.showrooms;
      limit = limits.stores ?? 0;
      break;
    case "ai_credits": {
      // AI credits are tracked in ConciergeCreditsSummary (monthly + topUp, in dollars).
      // We model "exhausted" as `available <= 0` (monthly AND top-up both consumed).
      // `monthlyIncluded` is the plan limit; `current` = monthlyIncluded when exhausted,
      // else monthlyIncluded - monthly (monthly-used), progress reflects monthly allowance.
      const credits = summary.concierge?.credits;
      if (!credits) return null;
      const monthlyAllowance = credits.monthlyIncluded;
      if (monthlyAllowance <= 0) return null;
      const available = credits.available;
      const isOut = available <= 0;
      current = isOut
        ? monthlyAllowance
        : Math.max(0, monthlyAllowance - credits.monthly);
      limit = monthlyAllowance;
      break;
    }
    default:
      return null;
  }

  if (limit <= 0) return null;

  const pct = Math.min((current / limit) * 100, 100);

  return {
    current,
    limit,
    pct,
    isExhausted: current >= limit,
    isNear: pct > 80 && current < limit,
  };
}
